import React, { useCallback, useEffect, useRef, useState } from "react";
import { Editor } from "react-draft-wysiwyg";
import htmlToDraft from "html-to-draftjs";
import draftToHtml from "draftjs-to-html";
import { convertToRaw, ContentState, EditorState, Modifier, SelectionState } from "draft-js";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";

/**
 * MaillerEditor ne gère QUE le corps du mail (template + saisie + FAQ).
 * La signature est externalisée (hors EditorState) et doit être concaténée côté submit.
 *
 * AntD Form.Item injecte typiquement `value` et `onChange`.
 */
export default function MaillerEditor({ value, onChange, templateHtml, faqSelection }) {
	const [editorState, setEditorState] = useState(EditorState.createEmpty());

	// Ref stable sur onChange (évite les boucles / re-render)
	const onChangeRef = useRef(onChange);
	useEffect(() => {
		onChangeRef.current = onChange;
	}, [onChange]);

	// Dernier HTML émis vers le parent (permet de distinguer une MAJ externe vs interne)
	const lastEmittedHtmlRef = useRef("");

	const notifyParent = useCallback((newEditorState) => {
		if (!onChangeRef.current) return;
		const html = draftToHtml(convertToRaw(newEditorState.getCurrentContent()));
		lastEmittedHtmlRef.current = html;
		onChangeRef.current(html);
	}, []);

	const buildEditorStateFromHtml = useCallback((html) => {
		if (!html) return EditorState.createEmpty();

		return EditorState.createWithContent(
			ContentState.createFromBlockArray(
				htmlToDraft(html, (nodeName, node) => {
					if (nodeName === "img" && node instanceof HTMLImageElement) {
						if (node.parentElement instanceof HTMLDivElement) {
							const entityConfig = {};
							const img = node;
							entityConfig["src"] = img.getAttribute("src");
							entityConfig["alt"] = img.getAttribute("alt");

							entityConfig["height"] = img.style.height;
							entityConfig["width"] = img.style.width;

							return {
								type: "IMAGE",
								mutability: "MUTABLE",
								data: entityConfig,
							};
						}
					}
				})
			)
		);
	}, []);

	// 0) Sync value externe -> EditorState (reset du form, chargement initial, etc.)
	useEffect(() => {
		if (value == null) return; // AntD peut ne rien passer tant que le champ n'est pas touché
		const nextHtml = value || "";
		if (nextHtml === (lastEmittedHtmlRef.current || "")) return;

		const nextState = nextHtml ? buildEditorStateFromHtml(nextHtml) : EditorState.createEmpty();
		setEditorState(nextState);
	}, [value, buildEditorStateFromHtml]);

	// 1) Sélection d'un template => on remplace le corps (sans signature)
	useEffect(() => {
		if (templateHtml == null) return;

		const newState = templateHtml
			? buildEditorStateFromHtml(templateHtml)
			: EditorState.createEmpty();

		setEditorState(newState);
		notifyParent(newState);
	}, [templateHtml, buildEditorStateFromHtml, notifyParent]);

	// 2) Saisie utilisateur
	const handleEditorStateChange = useCallback(
		(newEditorState) => {
			setEditorState(newEditorState);
			notifyParent(newEditorState);
		},
		[notifyParent]
	);

	// 3) Insertion d'une FAQ (fragment HTML) à l'emplacement du curseur
	useEffect(() => {
		if (!faqSelection?.html) return;

		const currentEditorState = editorState;
		const blocksFromHtml = htmlToDraft(faqSelection.html);
		const contentState = ContentState.createFromBlockArray(
			blocksFromHtml.contentBlocks,
			blocksFromHtml.entityMap
		);
		const fragment = contentState.getBlockMap();

		const currentContent = currentEditorState.getCurrentContent();
		const currentSelection = currentEditorState.getSelection();

		const newContent = Modifier.replaceWithFragment(currentContent, currentSelection, fragment);

		let nextSelection = currentSelection;
		try {
			const lastInsertedKey = fragment.keySeq().last();
			if (lastInsertedKey) {
				const lastInsertedBlock = newContent.getBlockForKey(lastInsertedKey);
				if (lastInsertedBlock) {
					nextSelection = SelectionState.createEmpty(lastInsertedKey).merge({
						anchorOffset: lastInsertedBlock.getLength(),
						focusOffset: lastInsertedBlock.getLength(),
					});
				}
			}
		} catch (e) {}

		const pushed = EditorState.push(currentEditorState, newContent, "insert-fragment");
		const withSelection = EditorState.forceSelection(pushed, nextSelection);

		setEditorState(withSelection);
		notifyParent(withSelection);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [faqSelection]);

	return (
		<Editor
			editorState={editorState}
			onEditorStateChange={handleEditorStateChange}
			wrapperClassName="demo-wrapper"
			editorClassName="demo-editor"
			editorStyle={{ maxHeight: "400px", overflowY: "auto" }}
			toolbarClassName="demo-toolbar"
			toolbar={{
				options: ["inline", "blockType", "fontSize", "fontFamily"],
				inline: {
					options: ["bold", "italic", "underline", "strikethrough", "monospace"],
					bold: { className: "bordered-option-classname" },
					italic: { className: "bordered-option-classname" },
					underline: { className: "bordered-option-classname" },
					strikethrough: { className: "bordered-option-classname" },
					code: { className: "bordered-option-classname" },
				},
				blockType: { className: "bordered-option-classname" },
				fontSize: { className: "bordered-option-classname" },
				fontFamily: { className: "bordered-option-classname" },
			}}
		/>
	);
}
